import React, { createContext, useState, useContext, useCallback, ReactNode, useEffect } from 'react';
import {
  signIn as apiSignIn,
  signUp as apiSignUp,
  getNonce,
  login as apiLogin,
  logout as apiLogout,
  setAuthToken,
  clearAuthToken
} from '../services/api';

import Web3 from 'web3';
import Web3Token from 'web3-token';
import { User, AuthContextType } from '../types';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [web3, setWeb3] = useState<Web3 | null>(null);
  const [address, setAddress] = useState<string | null>(null);

  const clearSession = useCallback(() => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('authToken');
    localStorage.removeItem('tokenExpiration');
    clearAuthToken();
  }, []);

  const checkExistingSession = useCallback(() => {
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('authToken');
    const expirationTime = localStorage.getItem('tokenExpiration');

    if (storedUser && storedToken && expirationTime) {
      const user = JSON.parse(storedUser);
      if (new Date().getTime() < parseInt(expirationTime)) {
        setUser(user);
        setAuthToken(storedToken);
        setAddress(user.address);
      } else {
        // Token has expired, clear the session
        clearSession();
      }
    }
    setLoading(false);
  }, [clearSession]);

  useEffect(() => {
    const initWeb3 = async () => {
      if (window.ethereum) {
        const web3Instance = new Web3(window.ethereum);
        setWeb3(web3Instance);
      }
    };

    initWeb3();
    checkExistingSession();
  }, [checkExistingSession]);

  const disconnectWallet = useCallback(() => {
    setAddress(null);
    // If you need to disconnect from the provider, add that logic here
  }, []);

  const setSession = (user: User) => {
    const expirationTime = new Date().getTime() + 24 * 60 * 60 * 1000; // 24 hours from now
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('authToken', user.token);
    localStorage.setItem('tokenExpiration', expirationTime.toString());
    setAuthToken(user.token);
    setUser(user);
    setAddress(user.address);
  };

  const signIn = useCallback(async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    try {
      const userData = await apiSignIn({ email, password });
      setSession(userData);
    } catch (err) {
      setError('Failed to sign in');
    } finally {
      setLoading(false);
    }
  }, []);

  const signUp = useCallback(async (email: string, password: string, username: string) => {
    setLoading(true);
    setError(null);
    try {
      const userData = await apiSignUp({ email, password, username });
      setSession(userData);
    } catch (err) {
      setError('Failed to sign up');
    } finally {
      setLoading(false);
    }
  }, []);

  const signOut = useCallback(async () => {
    setLoading(true);
    try {
      await apiLogout();
      clearSession();
    } catch (err) {
      setError('Failed to sign out');
    } finally {
      setLoading(false);
    }
  }, [clearSession]);

  const connectWallet = useCallback(async () => {
    if (!web3) {
      setError('Web3 is not initialized');
      return;
    }
    try {
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      const accounts = await web3.eth.getAccounts();
      setAddress(accounts[0]);
      setError(null);
    } catch (error: unknown) {
      if (error instanceof Error && 'code' in error && error.code === 4001) {
        // EIP-1193 userRejectedRequest error
        console.log('User rejected the connection request');
        clearSession();
        setError('Wallet connection was rejected');
      } else {
        console.error('Failed to connect wallet:', error);
        setError('Failed to connect wallet');
      }
    }
  }, [web3, clearSession]);

  const loginWithWallet = useCallback(async () => {
    if (!web3 || !address) {
      setError('Please connect your wallet first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { nonce, statement } = await getNonce(address);
      const token = await Web3Token.sign(

        async (msg: string) => {
          try {
            return await web3.eth.personal.sign(msg, address, '')
          } catch (error: unknown) {
            if (error instanceof Error && 'code' in error && error.code === 4001) {
              // User denied message signature
              throw new Error('User rejected the signature request');
            }
            throw error;
          }
        },
        {
          nonce,
          statement: `${statement}:${nonce}`,
          expires_in: '1d',
          not_before: new Date(),
        }
      );
      const { authToken, user } = await apiLogin(token);
      let userData = user
      userData.token = authToken
      userData.address = address

      setSession(userData);
    } catch (error: unknown) {
      if (error instanceof Error && (error.message === 'User rejected the signature request' || error.message === 'User rejected the request.')) {
        console.error(error.message);

        clearSession();
        setError('Login was rejected. Please try again.');
      } else {
        setError('Failed to login with wallet');
      }
    } finally {
      setLoading(false);
    }
  }, [web3, address, clearSession]);

  const value = {
    user,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    connectWallet,
    disconnectWallet,
    loginWithWallet,
    address,
    isWalletConnected: !!address,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};